import {
  aggregateLineTransactions,
  ChargePayerChainEntry,
  LineTransactionLike,
} from 'ts-common/utils/serviceLineBalances.utils'
import { DEFAULT_WRITE_OFF_REASON, writeOffNeedsReason } from 'ts-common/utils/writeOffReasons.utils'

// What a hand posting already knows before the biller reads the EOB. Both
// manual doors — the inline Apply Payment row and the Create Payment modal —
// fill in from here, so a payment starts out the same whichever one opened.

const toNum = (value: string | number | null | undefined): number => Number(value || 0) || 0
const round2 = (value: number): number => Number(value.toFixed(2))

export type PostableLine = {
  charged?: number | string | null
  expectedAllowed?: number | null
  transactions?: ReadonlyArray<LineTransactionLike> | null
}

/**
 * Amount still transferred to the downstream payer — a downstream posting
 * starts from it. Read from the deepest payer that has posted: once the
 * downstream payer adjudicates, the transfer it answered is settled.
 */
export const transferredBalanceOf = (line: PostableLine): number =>
  Math.max(0, round2(aggregateLineTransactions(line.transactions).transfers))

export type PrefilledAmounts = {
  /** What this payer is being asked about: the charge for the primary, the transferred balance downstream. */
  baseCharged: number
  allowed: number
  writeOff: number
  writeOffReason: string
}

/** What is left of the amount the payer was asked about once it allows `allowed`. */
export const writeOffFromAllowed = (
  base: string | number | null | undefined,
  allowed: string | number | null | undefined,
): number => Math.max(0, round2(toNum(base) - toNum(allowed)))

/**
 * The write-off a remittance line opens with when the payer stated none: balance
 * minus allowed. Null leaves the line as it is — a stated write-off, a denial
 * (allowed $0), or no known balance.
 */
export const openingWriteOff = (
  balance: number | null | undefined,
  line: { allowed?: string | number | null; writeOff?: string | number | null },
): number | null => {
  if (balance === null || balance === undefined || toNum(line.writeOff) !== 0 || toNum(line.allowed) <= 0) {
    return null
  }

  const writeOff = writeOffFromAllowed(balance, line.allowed)

  return writeOff > 0 ? writeOff : null
}

// A write-off that needs a reason keeps the one picked, else the contractual
// default; one that needs none carries none.
export const writeOffReasonFor = (writeOff: number, currentReason: string | null | undefined): string =>
  writeOffNeedsReason(writeOff) ? currentReason || DEFAULT_WRITE_OFF_REASON : ''

// The primary expects the contracted rate (falling back to charged) with the
// contractual gap written off; a downstream payer starts from what was
// transferred to it, nothing written off. Paid and PR are the biller's to
// enter.
export const prefillAmounts = (isPrimary: boolean, line: PostableLine): PrefilledAmounts => {
  const charged = toNum(line.charged)
  const transferred = transferredBalanceOf(line)
  const allowed = isPrimary ? line.expectedAllowed ?? charged : transferred
  const writeOff = isPrimary ? writeOffFromAllowed(charged, allowed) : 0

  return {
    baseCharged: isPrimary ? charged : transferred,
    allowed: round2(allowed),
    writeOff,
    // The pre-filled gap IS the contractual one, so the routine paper EOB is a
    // confirm-and-save; the biller can still change it.
    writeOffReason: writeOff > 0 ? DEFAULT_WRITE_OFF_REASON : '',
  }
}

// The payer whose turn it is: with a transferred balance waiting on any of the
// lines, the first chain entry after the highest position that has already
// posted; otherwise the chain's head.
export const defaultChainPayer = (
  chain: ChargePayerChainEntry[],
  lines: ReadonlyArray<PostableLine>,
): ChargePayerChainEntry | undefined => {
  if (!chain.length) {
    return undefined
  }

  if (!lines.some((line) => transferredBalanceOf(line) > 0)) {
    return chain[0]
  }

  // A payment reversed in full posted nothing.
  const maxPostedPosition = lines.reduce(
    (max, line) => (line.transactions || []).reduce(
      (lineMax, tx) => (tx.paymentReversedAt ? lineMax : Math.max(lineMax, Number(tx.payerPosition) || 0)),
      max,
    ),
    0,
  )

  return chain.find((entry) => Number(entry.position) > maxPostedPosition) ?? chain[0]
}

export type ChargeClaimNumbers = {
  claimControlNumber?: string | null
  secondaryClaimControlNumber?: string | null
  tertiaryClaimControlNumber?: string | null
}

/**
 * The claim number a hand posting records: the control number the charge was
 * submitted under to the payer at that position — the same number an ERA for
 * it would echo back as CLP01. Empty when that claim was never submitted
 * electronically.
 */
export const chargeClaimNumberFor = (charge: ChargeClaimNumbers | null | undefined, position: number): string => {
  if (!charge) {
    return ''
  }

  if (position === 2) {
    return charge.secondaryClaimControlNumber || ''
  }

  if (position >= 3) {
    return charge.tertiaryClaimControlNumber || ''
  }

  return charge.claimControlNumber || ''
}

/** Today as the YYYY-MM-DD a date input takes. */
export const todayYyyyMmDd = (): string => {
  const now = new Date()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')

  return `${now.getFullYear()}-${month}-${day}`
}
